import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import { Globe } from "@/components/ui/globe";
import { Separator } from "@/components/ui/separator";
import { Gallery6 } from "@/components/ui/gallery6";
import { ArrowRight, Shield, Headphones, Users, Lock, Zap, Phone, Settings, Wrench, GraduationCap, Rocket, ShieldCheck, User, RefreshCw, Link, UserCheck, Building2, Target, BookOpen, Database, Share2, Calendar, Key, Globe as GlobeIcon, Quote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { navigateToEnterpriseDemo, navigateToEnterpriseForm } from "@/utils/navigation";
import { motion } from "motion/react";
import { getPublicImage } from "@/utils/imageUtils";
import contentFlowImage from "@/assets/enterprise-content-flow.jpg";
import platformCostsImage from "@/assets/enterprise-platform-costs.jpg";
import marketingExpertiseImage from "@/assets/enterprise-marketing-expertise.jpg";
import creationTeamImage from "@/assets/desks-creation-team.jpg";
import humanExpertiseImage from "@/assets/desks-human-expertise.jpg";
import flexibleScalingImage from "@/assets/desks-flexible-scaling.jpg";
import { ROICalculatorBanner } from "@/components/ROICalculatorBanner";
import { AccordionFeatureSection } from "@/components/ui/accordion-feature-section";
import FusionBentoCards from "@/components/ui/fusion-bento-cards";
import EnterpriseQuoteForm from "@/components/EnterpriseQuoteForm";
import { InfiniteSlider } from "@/components/ui/infinite-slider";
import { ProgressiveBlur } from "@/components/ui/progressive-blur";
import { CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
import rocketIcon from "@/assets/enterprise-rocket-icon.png";
import cloudGearIcon from "@/assets/enterprise-cloud-gear-icon.png";
import handshakeIcon from "@/assets/enterprise-handshake-icon.png";
import aiGrowthIcon from "@/assets/enterprise-ai-growth-icon.png";
import aiChaosImage from "@/assets/enterprise-ai-chaos.png";
import capacityBottleneckImage from "@/assets/enterprise-capacity-bottleneck.png";
import outcomesMetricImage from "@/assets/enterprise-outcomes-metric.png";
import sarahChen from "@/assets/testimonials/sarah-chen.jpg";
import marcusWilliams from "@/assets/testimonials/marcus-williams.jpg";
import priyaKapoor from "@/assets/testimonials/priya-kapoor.jpg";
import alexThompson from "@/assets/testimonials/alex-thompson.jpg";
import elenaRodriguez from "@/assets/testimonials/elena-rodriguez.jpg";
import davidPark from "@/assets/testimonials/david-park.jpg";

// API URL setup
let apiUrl = "https://api.gimmefy.ai/v2";
const baseUrl = window.location.origin;
if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}
interface Article {
  id: string;
  title: string;
  summary: string;
  url: string;
  image: string;
}
const EnterprisePage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // State for API articles
  const [apiArticles, setApiArticles] = useState<Article[]>([]);
  const [_isLoadingArticles, setIsLoadingArticles] = useState(true);
  const [isLogosHovered, setIsLogosHovered] = useState(false);

  // Strategic Pillars Data
  const pillarsData = [{
    number: 1,
    title: "Deliver Outcomes Quickly",
    description: "Ship better campaigns now. Not after six months of training and vendor negotiations.\n\nSpeed to market is your competitive edge.",
    icon: rocketIcon,
    bgColor: "bg-rose-100/80 dark:bg-rose-950/30"
  }, {
    number: 2,
    title: "Remove the Tech Burden",
    description: "Models, upgrades, integrations handled behind the scenes. Your team focuses on strategy and creativity.\n\nNo stack lock-in. No IT headaches.",
    icon: cloudGearIcon,
    bgColor: "bg-blue-100/80 dark:bg-blue-950/30"
  }, {
    number: 3,
    title: "Offer Flexible Involvement",
    description: "Your team works independently when they can. Experts step in when deadlines hit. Scale capacity up and down.\n\nNo long-term commitments, no staffing drama.",
    icon: handshakeIcon,
    bgColor: "bg-amber-100/80 dark:bg-amber-950/30"
  }, {
    number: 4,
    title: "Stay Best-in-Class",
    description: "The AI landscape evolves fast. Fusion evolves with it, automatically.\n\nStay current and tuned to your brand without constant rebuilds or budget battles.",
    icon: aiGrowthIcon,
    bgColor: "bg-emerald-100/80 dark:bg-emerald-950/30"
  }];
  useEffect(() => {
    const handleScrollToHash = () => {
      const hash = window.location.hash;
      if (hash) {
        const targetId = hash.substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
        }
      }
    };
    setTimeout(handleScrollToHash, 100);
  }, []);

  // Fetch articles from API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setIsLoadingArticles(true);
        const contentUrl = `${apiUrl}/contents/public?filters[group_name]=WEBSITE_RELATED_ARTICLES&findInSet[tags]=enterprise&sort=display_order`;
        const response = await fetch(contentUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch articles');
        }
        const data = await response.json();

        // Transform API data to match our Article interface
        if (data.success && data.payload && Array.isArray(data.payload)) {
          const transformedArticles: Article[] = data.payload.map((item: Record<string, unknown>, index: number) => {
            return {
              id: item.id as string || `article-${index + 1}`,
              title: item.name as string || 'Untitled Article',
              summary: item.content as string || 'No summary available',
              url: item.content2 as string || '#',
              image: item.image as string ? getPublicImage('frontend-files', item.image as string) || "/lovable-uploads/placeholder.svg" : "/lovable-uploads/placeholder.svg"
            };
          });
          setApiArticles(transformedArticles);
        }
      } catch (error) {
        console.error('Error fetching articles from API:', error);
        setApiArticles([]);
      } finally {
        setIsLoadingArticles(false);
      }
    };
    fetchArticles();
  }, []);
  const _features = [{
    icon: Headphones,
    title: "Priority Support",
    description: "24/7 live chat & a dedicated success manager—issue resolution in hours, not days.",
    bgColor: "bg-purple-100 dark:bg-purple-900/20",
    iconColor: "text-purple-600 dark:text-purple-400"
  }, {
    icon: Globe,
    title: "Custom Login URL",
    description: "YourBrand.ai or any sub-domain—complete white-label UI and SSO support.",
    bgColor: "bg-red-100 dark:bg-red-900/20",
    iconColor: "text-red-600 dark:text-red-400"
  }, {
    icon: Users,
    title: "Concierge Onboarding",
    description: "Hands-on workshops with our Customer Success team to train users & import assets.",
    bgColor: "bg-orange-100 dark:bg-orange-900/20",
    iconColor: "text-orange-600 dark:text-orange-400"
  }, {
    icon: Lock,
    title: "Org-Level Access Control",
    description: "Granular roles, audit logs, and workspace permissions—governance made simple.",
    bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
    iconColor: "text-yellow-600 dark:text-yellow-400"
  }, {
    icon: Zap,
    title: "Custom Playbooks & Agents",
    description: "We build bespoke stacks, integrations, and Agentic frameworks to match your workflow.",
    bgColor: "bg-green-100 dark:bg-green-900/20",
    iconColor: "text-green-600 dark:text-green-400"
  }];
  const _customizationFeatures = [{
    icon: Link,
    title: "Custom URL",
    description: "Launch your brand on a dedicated subdomain: xxx.gimmefy.ai.",
    bgColor: "bg-blue-100 dark:bg-blue-900/20",
    iconColor: "text-blue-600 dark:text-blue-400",
    hoverGradient: "from-blue-700/90 to-blue-900/90"
  }, {
    icon: UserCheck,
    title: "Custom Onboarding",
    description: "Onboarding timed and tailored to your organisation's needs.",
    bgColor: "bg-green-100 dark:bg-green-900/20",
    iconColor: "text-green-600 dark:text-green-400",
    hoverGradient: "from-green-700/90 to-green-900/90"
  }, {
    icon: Building2,
    title: "Workspace Setup",
    description: "Workspaces structured to mirror your teams and departments.",
    bgColor: "bg-purple-100 dark:bg-purple-900/20",
    iconColor: "text-purple-600 dark:text-purple-400",
    hoverGradient: "from-purple-700/90 to-purple-900/90"
  }, {
    icon: Target,
    title: "Project Setup",
    description: "Our customer success team helps you kick off key projects—fast.",
    bgColor: "bg-red-100 dark:bg-red-900/20",
    iconColor: "text-red-600 dark:text-red-400",
    hoverGradient: "from-red-700/90 to-red-900/90"
  }, {
    icon: Shield,
    title: "Brand Vault Guidance",
    description: "Guided setup to lock in your brand assets and voice, with expert support.",
    bgColor: "bg-orange-100 dark:bg-orange-900/20",
    iconColor: "text-orange-600 dark:text-orange-400",
    hoverGradient: "from-orange-700/90 to-orange-900/90"
  }, {
    icon: BookOpen,
    title: "Custom Playbook",
    description: "Get one bespoke workflow playbook built just for your process.",
    bgColor: "bg-teal-100 dark:bg-teal-900/20",
    iconColor: "text-teal-600 dark:text-teal-400",
    hoverGradient: "from-teal-700/90 to-teal-900/90"
  }, {
    icon: Database,
    title: "Data Integrations (Optional)",
    description: "Connect your own data sources for seamless workflow.",
    bgColor: "bg-indigo-100 dark:bg-indigo-900/20",
    iconColor: "text-indigo-600 dark:text-indigo-400",
    hoverGradient: "from-indigo-700/90 to-indigo-900/90"
  }, {
    icon: Share2,
    title: "Publishing Integrations (Optional)",
    description: "Plug into your preferred channels for one-click publishing.",
    bgColor: "bg-pink-100 dark:bg-pink-900/20",
    iconColor: "text-pink-600 dark:text-pink-400",
    hoverGradient: "from-pink-700/90 to-pink-900/90"
  }];
  const _onboardingSteps = [{
    icon: Phone,
    title: "Discovery Call",
    description: "map your brand, workflows, and security requirements",
    color: "bg-blue-500"
  }, {
    icon: Settings,
    title: "White-Label Setup",
    description: "custom domain, UI skin, SSO, Brand Vault configuration",
    color: "bg-green-500"
  }, {
    icon: Wrench,
    title: "Custom Build",
    description: "bespoke Agents, Playbooks, and integrations",
    color: "bg-red-500"
  }, {
    icon: GraduationCap,
    title: "Team Training",
    description: "live sessions & tailored knowledge base",
    color: "bg-orange-500"
  }, {
    icon: Rocket,
    title: "Launch & Evolve",
    description: "dedicated CSM monitors usage and optimises continuously",
    color: "bg-purple-500"
  }];
  const team = [{
    name: "Shalu Wasu",
    role: "CEO",
    location: "Singapore",
    image: "/lovable-uploads/3bcd8ec8-f00b-4df7-acd8-623807ecb00c.png",
    bookingUrl: "https://cal.read.ai/gimmefy-sw/60-min"
  }, {
    name: "Mahima Puri",
    role: "Regional Director, SEA",
    subtitle: "Client Relations & Growth",
    location: "Singapore",
    image: "/lovable-uploads/4bfc08f1-71f5-4b44-ae11-2108eddd1024.png",
    bookingUrl: "https://cal.read.ai/mahima-rwt49"
  }, {
    name: "Niharika Gulati",
    role: "Regional Director, APAC",
    subtitle: "Client Relations & Growth",
    location: "Singapore",
    image: "/lovable-uploads/97e74ebf-a9a0-4dbc-8496-ff3ca90887aa.png",
    bookingUrl: "https://cal.read.ai/niha-5ag0x/60-min?step=date-and-time"
  }, {
    name: "Bert van den Berg",
    role: "Regional Director, Europe",
    subtitle: "Client Relations & Growth",
    location: "Amsterdam",
    image: "/lovable-uploads/f7da40e0-a08d-4254-933e-90d990e19bf1.png",
    bookingUrl: "https://cal.read.ai/bert-van-den-berg?step=duration"
  }];

  // Ring colors for team member avatars
  const ringColors = ["bg-gradient-to-br from-red-400 to-orange-500",
  // Shalu
  "bg-gradient-to-br from-pink-400 to-fuchsia-500",
  // Mahima
  "bg-gradient-to-br from-blue-400 to-indigo-500",
  // Niharika
  "bg-gradient-to-br from-teal-400 to-cyan-500" // Bert
  ];

  // Pattern generation for grid background
  const _genRandomPattern = (length = 5) => {
    return Array.from({
      length
    }, () => [Math.floor(Math.random() * 4) + 7, Math.floor(Math.random() * 6) + 1]);
  };
  const _GridPattern = ({
    width,
    height,
    x,
    y,
    squares,
    ...props
  }: React.SVGProps<SVGSVGElement> & {
    width: number;
    height: number;
    x: number;
    y: number;
    squares?: [number, number][];
  }) => {
    const patternId = `grid-pattern-${Math.random().toString(36).substr(2, 9)}`;
    return <svg aria-hidden="true" {...props}>
      <defs>
        <pattern id={patternId} width={width} height={height} patternUnits="userSpaceOnUse" x={x} y={y}>
          <path d={`M.5 ${height}V.5H${width}`} fill="none" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" strokeWidth={0} fill={`url(#${patternId})`} />
      {squares && <svg x={x} y={y} className="overflow-visible animate-pulse [animation-duration:12s] opacity-50">
        {squares.map(([x, y]: [number, number], index: number) => <rect strokeWidth="0" key={index} width={width + 1} height={height + 1} x={x * width} y={y * height} />)}
      </svg>}
    </svg>;
  };
  return <div className="min-h-screen bg-background">
    <Navigation />

    {/* Hero Section */}
    <section id="enterprise-overview" className="py-20 md:py-28 bg-background relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:gap-16 items-center lg:items-start">
          {/* Text Content - Left Side */}
          <div className="flex-1 lg:max-w-xl text-center lg:text-left">
            <motion.div className="space-y-6" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }}>
              <div className="space-y-4">
            <h1 id="enterprise-hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              The Power of <span className="bg-gradient-primary bg-clip-text text-transparent">gimmefy Fusion</span> for Enterprises
            </h1>
                <p className="text-xl md:text-2xl font-medium text-foreground/80">
                  Your marketing team's unfair advantage at enterprise scale
                </p>
              </div>
              
              <motion.p id="enterprise-hero-description" className="text-lg text-muted-foreground leading-relaxed" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2,
                duration: 0.6
              }}>Global campaigns. Regional teams. Compliance hoops. </motion.p>
              
              <motion.p className="text-lg text-muted-foreground leading-relaxed" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3,
                duration: 0.6
              }}>
                Fusion helps you overcome them with structure to scale, edge from AI, and lift from experts in one system.
              </motion.p>

              <motion.div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 pt-4" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4,
                duration: 0.6
              }}>
                <Button id="enterprise-request-demo-button" variant="hero" size="lg" className="group" onClick={() => navigateToEnterpriseDemo(navigate)}>
                  Talk to our Enterprise Team
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.5,
                  duration: 0.6
                }}>
                  <Button id="enterprise-see-fusion-button" variant="ghost" size="lg" className="group" onClick={() => navigateToEnterpriseForm(navigate)}>
                    See how Fusion works
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          {/* Video - Right Side */}
          <motion.div className="flex-1 mt-12 lg:mt-0 w-full" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.3,
            duration: 0.8
          }}>
            <div className="relative">
              <div className="relative w-full" style={{
                paddingBottom: '56.25%'
              }}>
                <iframe className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg" src="https://www.youtube.com/embed/FIsT2bkDLBw?si=oOpf-OYCx5dJ4lkh&autoplay=1&mute=1&rel=0&loop=1&playlist=FIsT2bkDLBw" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Client Logos Section */}
    <section className="py-12 bg-background">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto cursor-pointer relative" onClick={() => navigate('/customer-stories')} onMouseEnter={() => setIsLogosHovered(true)} onMouseLeave={() => setIsLogosHovered(false)} role="link" tabIndex={0} aria-label="View customer stories" onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/customer-stories');
          }
        }}>
          {/* Trusted by heading */}
          <div className="text-center mb-8">
            <p className="text-sm font-medium text-muted-foreground tracking-wide uppercase">Trusted by</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-8 md:gap-12 items-center justify-items-center">
            {/* Row 1 */}
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/c078e241-3c0b-4b43-9ca7-463f4a0b7d43.png" alt="Novotel" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">Novotel</div>';
              }} />
            </div>
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/6f72231a-02bb-4284-8f6b-be1f16570b92.png" alt="Unilever" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">Unilever</div>';
              }} />
            </div>
            <div className="flex items-center justify-center h-12">
              <img src="https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/6cee356a-106e-4a74-a662-ec970f3dfe61/70188ecd-831e-4576-b8df-a46d7bbf4017.png?content-type=image%2Fpng" alt="Ignyte" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">Ignyte</div>';
              }} />
            </div>
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/a736fac8-3234-4821-83ec-c67e969de299.png" alt="Knight Frank" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">Knight Frank</div>';
              }} />
            </div>
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/befabc60-5300-4214-b167-e916f1447aef.png" alt="Motul" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">Motul</div>';
              }} />
            </div>

            {/* Row 2 */}
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/80fd16dd-54fd-4733-88ee-d9ec5226134d.png" alt="BTDT" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">BTDT</div>';
              }} />
            </div>
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/74da15ff-3ab9-4eee-8b91-1255f3e7ad9b.png" alt="Weber Shandwick" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">Weber Shandwick</div>';
              }} />
            </div>
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/8a04475a-011c-422b-a117-3d3729d73287.png" alt="Deem" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">Deem</div>';
              }} />
            </div>
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/9c9fb851-8586-44cc-94ad-e7138829e299.png" alt="IMDA" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">IMDA</div>';
              }} />
            </div>
            <div className="flex items-center justify-center h-12">
              <img src="/lovable-uploads/d3ec5ab5-d465-4379-ac1b-cd993acabda7.png" alt="Claritas" width={100} height={32} className="h-8 w-auto object-contain filter grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300" onError={e => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="text-lg font-semibold text-muted-foreground">Claritas</div>';
              }} />
            </div>
          </div>

          {/* Hover overlay button */}
          {isLogosHovered && <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-40 rounded-lg transition-all">
              <span className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium shadow-lg">
                Read their stories →
              </span>
            </div>}
        </div>
      </div>
    </section>

    {/* Why gimmefy Fusion exists Section */}
      <AccordionFeatureSection badge="The Challenge" heading={<>Enterprise Marketing Is <span className="bg-gradient-primary bg-clip-text text-transparent">Changing</span></>} subheading="You're not looking for another AI toy. You want a system that ties expertise, AI scale, and your proven processes together. Fast, on-brand, under control." features={[{
      id: 1,
      title: "AI Chaos is Common",
      image: aiChaosImage,
      description: "Disconnected tools. Research in one place, ideation in another, creation somewhere else. Your marketers juggle logins instead of creating. The workflow is broken.\n\nYou need a system, not more chaos."
    }, {
      id: 2,
      title: "Capacity is the Bottleneck",
      image: capacityBottleneckImage,
      description: "More campaigns, channels, markets, localization. Headcount isn't keeping pace. Ship fast but sacrifice quality, or maintain standards but miss windows. Your team is maxed out.\n\nYou need to multiply capacity without multiplying headcount."
    }, {
      id: 3,
      title: "Outcomes are the Only Metric",
      image: outcomesMetricImage,
      description: "Your board cares about market share, pipeline velocity, brand equity, and ROI. Not your tech stack. You're measured on outcomes.\n\nEvery hour managing tech is an hour not driving results."
    }]} />

    {/* The Opportunity - 4 Strategic Pillars */}
    <section className="py-20 lg:py-28 bg-muted/30">
      <div className="container mx-auto px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-primary/10 to-emerald-500/10 text-primary border border-primary/20 mb-4">
              The Opportunity
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              The Answer? A  <span className="bg-gradient-primary bg-clip-text text-transparent">True Brand Engine </span>.
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">Bolting on more tools won't fix this. You deserve a system that works with you to achieve four strategic goals.</p>
          </motion.div>

          {/* Cards - Desktop: 4-column grid, Mobile: Infinite auto-scroll */}
          {isMobile ? <InfiniteSlider gap={16} duration={20} className="py-4">
              {pillarsData.map((pillar, index) => <div key={index} className={`rounded-2xl p-6 min-w-[280px] ${pillar.bgColor}`}>
                  <img src={pillar.icon} alt={pillar.title} className="h-14 w-14 mb-5" />
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {pillar.description}
                  </p>
                </div>)}
            </InfiniteSlider> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {pillarsData.map((pillar, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1 * (index + 1)
            }} className={`rounded-2xl p-6 ${pillar.bgColor}`}>
                  <img src={pillar.icon} alt={pillar.title} className="h-14 w-14 mb-5" />
                  <h3 className="text-lg font-bold text-foreground mb-2">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {pillar.description}
                  </p>
                </motion.div>)}
            </div>}
        </div>
      </div>
    </section>


    {/* Team Capacity Section */}
    <section className="py-12 md:py-16 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center space-y-4 md:space-y-6 mb-8 md:mb-12 lg:mb-16">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-primary/10 to-emerald-500/10 text-primary border border-primary/20 mb-4">
              Your new workflow
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              Unlock your team's <span className="bg-gradient-primary bg-clip-text text-transparent">true capacity</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              With Fusion, your marketing team brings your vision to life, working better and faster while feeling empowered—not burned out.
            </p>
          </div>

          {/* Three Column Cards */}
          <div className="space-y-4 md:space-y-6 lg:grid lg:grid-cols-3 lg:gap-8 lg:space-y-0">
            {/* Card 1: End-to-End Content */}
            <div className="group bg-card rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 lg:overflow-hidden">
              <div className="lg:aspect-video lg:overflow-hidden lg:w-full hidden lg:block">
                <img src={contentFlowImage} alt="Content workflow from research to publishing" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
              <div className="p-4 md:p-5 lg:p-6 space-y-2 relative">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden float-right ml-3 mb-2 lg:hidden flex-shrink-0">
                  <img src={contentFlowImage} alt="Content workflow from research to publishing" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground">
                  End-to-End Content, One Flow
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Move from research to publishing in one workspace. Playbooks and QA keep every asset on-brand. You stay in control from start to finish.
                </p>
              </div>
            </div>

            {/* Card 2: One Platform, Lower Costs */}
            <div className="group bg-card rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 lg:overflow-hidden">
              <div className="lg:aspect-video lg:overflow-hidden lg:w-full hidden lg:block">
                <img src={platformCostsImage} alt="Platform consolidation and cost savings" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
              <div className="p-4 md:p-5 lg:p-6 space-y-2 relative">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden float-right ml-3 mb-2 lg:hidden flex-shrink-0">
                  <img src={platformCostsImage} alt="Platform consolidation and cost savings" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground">
                  One Platform, Lower Costs
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Replace your tool stack with one platform. Onboarding is simple and costs stay low as you manage everything in one place.
                </p>
              </div>
            </div>

            {/* Card 3: Instant Marketing Know-How */}
            <div className="group bg-card rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 lg:overflow-hidden">
              <div className="lg:aspect-video lg:overflow-hidden lg:w-full hidden lg:block">
                <img src={marketingExpertiseImage} alt="Marketing strategy and expertise" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </div>
              <div className="p-4 md:p-5 lg:p-6 space-y-2 relative">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden float-right ml-3 mb-2 lg:hidden flex-shrink-0">
                  <img src={marketingExpertiseImage} alt="Marketing strategy and expertise" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <h3 className="text-base md:text-lg lg:text-xl font-bold text-foreground">
                  Instant Marketing Know-How
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  Access expert strategy and creative power on demand. Bridge skill gaps and scale your vision without hiring.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* AI Lab Section */}
    <section id="ai-lab" className="py-20 lg:py-28 bg-gradient-to-br from-slate-50/50 via-background to-emerald-50/20 dark:from-slate-900/10 dark:via-background dark:to-emerald-900/5 border-t border-border/30">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="mb-8">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-primary/10 to-emerald-500/10 text-primary border border-primary/20">
              Custom AI 
            </span>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            {/* Left Column - Features */}
            <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="space-y-10">
              <div>
                <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight bg-gradient-to-r from-red-500 via-orange-500 to-red-400 bg-clip-text text-transparent mb-4">
                  AI Lab
                </h2>
                <h3 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">From using AI, to owning it.</h3>
                <p className="text-muted-foreground mt-4 leading-relaxed">
                  Enterprise teams need AI that understands brand rules, connects to knowledge bases, and integrates with existing tools. Securely, compliantly, without IT nightmares.
                </p>
              </div>

              <div className="space-y-3">
                {[{
                  icon: Database,
                  color: 'cyan',
                  title: 'Knowledge base, Secret Sauce',
                  description: 'Connect your knowledge base, product docs, brand guidelines, and campaigns. AI Lab generates content that references YOUR data, YOUR voice.'
                }, {
                  icon: Settings,
                  color: 'emerald',
                  title: 'Custom Agents and Workflows',
                  description: 'Build bespoke AI agents tailored to your framework, approval processes, and regional requirements. Fusion adapts to how YOU work.'
                }, {
                  icon: Link,
                  color: 'purple',
                  title: 'Custom Integrations',
                  description: 'Connect your CRM, DAM, analytics platforms directly into your workspace. Your team works in one system. No tab-switching, no manual transfers.'
                }].map((feature, index) => <motion.div key={feature.title} initial={{
                  opacity: 0,
                  x: -10
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: 0.15 + index * 0.1
                }} className="group flex items-start gap-4 p-4 -mx-4 rounded-xl hover:bg-muted/30 transition-colors duration-300">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg bg-${feature.color}-100 dark:bg-${feature.color}-900/30 flex items-center justify-center group-hover:scale-105 transition-transform duration-300`}>
                      <feature.icon className={`w-5 h-5 text-${feature.color}-600 dark:text-${feature.color}-400`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">{feature.title}</h4>
                      <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </motion.div>)}
                <p className="text-emerald-600 dark:text-emerald-400 font-medium text-sm pl-4">...and more</p>
              </div>
            </motion.div>

            {/* Right Column - Quote and Compliance */}
            <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="space-y-8">
              <div className="bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800/50 dark:to-slate-900/50 rounded-3xl p-8 lg:p-10 shadow-sm hover:shadow-md transition-shadow duration-300 border border-border/20">
                <p className="text-2xl md:text-4xl lg:text-6xl font-bold text-foreground leading-tight tracking-tight text-right">Serious AI, customized for your enterprise. Without you turning into an AI department.</p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {[{
                  icon: ShieldCheck,
                  label: 'SOC 2',
                  description: 'Type II certified',
                  gradient: 'from-emerald-500/20 to-green-500/20',
                  iconBg: 'bg-emerald-500/20',
                  iconColor: 'text-emerald-500'
                }, {
                  icon: Shield,
                  label: 'GDPR',
                  description: 'Fully compliant',
                  gradient: 'from-blue-500/20 to-cyan-500/20',
                  iconBg: 'bg-blue-500/20',
                  iconColor: 'text-blue-500'
                }, {
                  icon: Lock,
                  label: 'ISO 27001',
                  description: 'Certified secure',
                  gradient: 'from-purple-500/20 to-violet-500/20',
                  iconBg: 'bg-purple-500/20',
                  iconColor: 'text-purple-500'
                }].map((badge, index) => <motion.div key={badge.label} className={`group relative flex flex-col items-center justify-center text-center p-5 min-h-[120px] rounded-2xl bg-gradient-to-br ${badge.gradient} backdrop-blur-sm hover:shadow-lg transition-all duration-300`} initial={{
                  opacity: 0,
                  y: 10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.3,
                  delay: 0.1 + index * 0.05
                }} whileHover={{
                  scale: 1.02
                }}>
                    <div className={`relative w-10 h-10 rounded-xl ${badge.iconBg} flex items-center justify-center mb-2 transition-transform duration-300 group-hover:scale-110`}>
                      <badge.icon className={`h-5 w-5 ${badge.iconColor}`} />
                    </div>
                    <span className="font-semibold text-foreground text-sm mb-0.5">{badge.label}</span>
                    <span className="text-xs text-muted-foreground">{badge.description}</span>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>

    {/* Creation Desk Section */}
    <section id="creation-desk" className="py-20 lg:py-28 bg-gradient-to-br from-purple-50/30 via-background to-pink-50/20 dark:from-purple-900/5 dark:via-background dark:to-pink-900/5 border-t border-border/20">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="text-center mb-8">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-primary/10 to-emerald-500/10 text-primary border border-primary/20 mb-4">
              1X... 3X... 10X Capability.
            </span>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight bg-gradient-to-r from-red-500 via-orange-500 to-red-400 bg-clip-text text-transparent mb-4">
              Creation Desk
            </h2>
            <p className="text-xl md:text-2xl text-muted-foreground mb-4">
              Your on-demand creative strike team
            </p>
            
            {/* Process Flow */}
            <div className="inline-flex flex-wrap items-center justify-center gap-2 md:gap-3 bg-muted/20 rounded-2xl px-6 py-4">
              {['Brief', 'Create', 'QA', 'Deliver', 'Repeat'].map((step, index) => <div key={step} className="flex items-center gap-2 md:gap-3">
                  <span className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 cursor-default ${index === 0 ? 'bg-blue-100/80 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : index === 1 ? 'bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : index === 2 ? 'bg-amber-100/80 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : index === 3 ? 'bg-purple-100/80 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-pink-100/80 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300'}`}>
                    {step}
                  </span>
                  {index < 4 && <ArrowRight className="w-4 h-4 text-muted-foreground/60" />}
                </div>)}
            </div>
            
            <p className="text-muted-foreground mt-4 max-w-3xl mx-auto leading-relaxed">
              When deadlines hit or campaigns stack up, Creation Desk gives you instant capacity. Expert users work inside your workspace to deliver brand-ready assets faster than agencies or freelancer networks.
            </p>
            
            <Separator className="mt-8 max-w-md mx-auto opacity-30" />
          </motion.div>

          {/* Three Image Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[{
              src: creationTeamImage,
              alt: 'Team collaboration',
              text: "We're expert users who sit <em>inside</em> your gimmefy workspace,"
            }, {
              src: humanExpertiseImage,
              alt: 'Human expertise',
              text: 'helping to create brand-ready assets <em>better</em>, and faster.'
            }, {
              src: flexibleScalingImage,
              alt: 'Flexible scaling',
              text: 'Flexible and cost-effective; dial <em>hours</em> up & down as you need to.'
            }].map((card, index) => <motion.div key={card.alt} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1 + index * 0.1
            }} className="group relative rounded-3xl overflow-hidden aspect-[4/3] shadow-lg hover:shadow-xl transition-shadow duration-300 ring-1 ring-border/10">
                <img src={card.src} alt={card.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <p className="text-white text-lg font-semibold" dangerouslySetInnerHTML={{
                  __html: card.text.replace(/<em>/g, '<em class="not-italic font-bold">')
                }} />
                </div>
              </motion.div>)}
          </div>

          {/* Bottom Feature Strip */}
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="bg-gradient-to-br from-purple-100/60 to-pink-50/60 dark:from-purple-900/15 dark:to-pink-900/15 rounded-3xl p-8 lg:p-10 shadow-sm">
            <div className="grid lg:grid-cols-5 gap-8">
              {/* Intro text */}
              <div className="lg:col-span-1">
                <p className="text-foreground font-medium leading-relaxed">
                  Powered by the expert users who <em>built gimmefy</em>, backed by <em>real-world marketing experience</em>.
                </p>
              </div>

              {/* Four service cards */}
              <div className="lg:col-span-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[{
                  icon: Rocket,
                  color: 'blue',
                  title: 'Setting the foundation right',
                  description: "We'll set up your Brand Vault and build ready-to-use templates so your team hits the right tone from day one."
                }, {
                  icon: RefreshCw,
                  color: 'emerald',
                  title: 'gimmefy maintenance',
                  description: 'Ongoing checks, template refreshes and short refresher sessions to keep everyone confident and on brand.'
                }, {
                  icon: Settings,
                  color: 'purple',
                  title: 'Customizing the platform',
                  description: "We'll plug gimmefy into your platforms and tailor workspaces so your processes feel effortless."
                }, {
                  icon: Users,
                  color: 'amber',
                  title: 'Hands-on support',
                  description: 'Our experts help create or polish campaign assets with you—fast, collaborative and on brand.'
                }].map((service, index) => <motion.div key={service.title} initial={{
                  opacity: 0,
                  y: 15
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: 0.35 + index * 0.08
                }} className="group bg-background/80 backdrop-blur-sm rounded-xl p-5 space-y-2 shadow-sm hover:shadow-md transition-shadow duration-300 ring-1 ring-border/10">
                    <div className={`w-8 h-8 rounded-lg bg-${service.color}-100 dark:bg-${service.color}-900/30 flex items-center justify-center lg:group-hover:opacity-0 transition-opacity duration-300`}>
                      <service.icon className={`w-4 h-4 text-${service.color}-600 dark:text-${service.color}-400`} />
                    </div>
                    <h4 className="font-semibold text-foreground text-sm lg:text-lg lg:group-hover:opacity-0 transition-opacity duration-300">{service.title}</h4>
                    <p className="text-muted-foreground text-xs leading-relaxed opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300 absolute inset-0 flex items-center justify-center p-5">{service.description}</p>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* How gimmefy Fusion lands Section */}
    <FusionBentoCards />

    {/* Why Teams Love gimmefy */}
    <section className="py-24 bg-muted/30">
      <div className="container mx-auto px-6">
        <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Why teams love gimmefy</h2>
        </motion.div>
        
        <div className="relative mb-12">
          {/* Left edge blur - hidden on mobile for readability */}
          <div className="absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none hidden lg:block">
            <ProgressiveBlur direction="right" blurLayers={6} className="h-full" />
          </div>
          
          {/* Right edge blur - hidden on mobile for readability */}
          <div className="absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none hidden lg:block">
            <ProgressiveBlur direction="left" blurLayers={6} className="h-full" />
          </div>
          
          {/* Infinite Slider */}
          <InfiniteSlider duration={35} durationOnHover={55} gap={24} direction="horizontal" className="py-4">
            {[{
              quote: "We plugged Creation Desk into our workflow and started shipping content the same day. No setup, no migration, just instant output.",
              name: "Sarah Chen",
              position: "Content Marketing Manager",
              avatar: sarahChen
            }, {
              quote: "Creation Desk gave us a full content team without the headcount. No onboarding, no performance reviews, no HR headaches.",
              name: "Marcus Williams",
              position: "Chief Marketing Officer",
              avatar: marcusWilliams
            }, {
              quote: "After training gimmefy on our brand voice, everything comes back sounding like us. No more panicking about what AI might say on our behalf.",
              name: "Priya Kapoor",
              position: "Brand Director",
              avatar: priyaKapoor
            }, {
              quote: "With AI Lab, I can see exactly what's happening at every step. No black box, no surprises, just full control.",
              name: "Alex Thompson",
              position: "Head of Marketing Operations",
              avatar: alexThompson
            }, {
              quote: "We went from one market to twelve without adding headcount. Creation Desk just scales with us.",
              name: "Elena Rodriguez",
              position: "VP Global Marketing",
              avatar: elenaRodriguez
            }, {
              quote: "The team behind AI Lab already built marketing AI for Unilever and DBS. That track record gave us confidence to bet on them.",
              name: "David Park",
              position: "Head of Digital Strategy",
              avatar: davidPark
            }].map((testimonial, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="w-80 lg:w-96">
                <Card className="h-full hover:shadow-xl transition-all hover:scale-[1.02] border-border/30 bg-gradient-to-br from-violet-500/5 via-purple-500/5 to-fuchsia-500/5">
                  <CardContent className="pt-8 pb-8 px-8">
                    {/* Quote icon */}
                    <Quote className="w-10 h-10 text-primary/40 mb-4" />
                    
                    {/* Testimonial quote */}
                    <p className="text-base text-foreground mb-6 leading-relaxed">
                      "{testimonial.quote}"
                    </p>
                    
                    {/* Attribution */}
                    <div className="border-t border-border/30 pt-4 flex items-center gap-4">
                      <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-primary/20" />
                      <div>
                        <p className="font-semibold text-foreground">{testimonial.name}</p>
                        <p className="text-sm text-muted-foreground">{testimonial.position}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </InfiniteSlider>
        </div>
      </div>
    </section>

    {/* ROI Calculator Banner */}
    <ROICalculatorBanner />


    {/* Contact Sales Team Section */}
    <section id="meet-the-crew" className="py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Ready to customise your gimmefy Fusion?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">Book your personalised session to discover how this platform puts you in control, harnessing AI to bring your vision to life and unlock your next phase of growth.</p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.map((member, index) => <div key={member.name} className="flex flex-col items-center text-center">
              {/* Circular image with colored ring */}
              <div className={`w-40 h-40 rounded-full p-1 mb-6 ${ringColors[index]}`}>
                <img src={member.image} alt={member.name} className="w-full h-full object-cover rounded-full bg-background" />
              </div>
              
              <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
              <p className="text-primary font-medium mb-1">{member.role}</p>
              {member.subtitle && <p className="text-sm text-muted-foreground mb-1">{member.subtitle}</p>}
              <p className="text-sm text-muted-foreground mb-4">{member.location}</p>
              
              <Button variant="outline" asChild className="gap-2">
                <a href={member.bookingUrl} target="_blank" rel="noopener noreferrer">
                  <Calendar className="w-4 h-4" />
                  Book a session
                </a>
              </Button>
            </div>)}
        </div>
      </div>
    </section>

    {/* Enterprise Form Section */}
    <section id="enterprise-form" className="py-20 lg:py-28 bg-gradient-to-b from-background to-muted/30 border-t border-border/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Column - Header */}
          <div className="space-y-4">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-primary/10 to-emerald-500/10 text-primary border border-primary/20">
              Get Started
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              Unlock your free Enterprise sandbox account
            </h2>
            <p className="text-lg text-muted-foreground">
              Prefer to explore gimmefy on your own? Fill in the form to get a complimentary sandbox account for your organisation. Unlock the platform and start bringing your ideas to life, at your own pace.
            </p>
          </div>
          
          {/* Right Column - Form */}
          <div>
            <EnterpriseQuoteForm />
          </div>
        </div>
      </div>
      </section>

      {/* Security & Trust Section - Positioned after contact form */}
      <section id="security-trust-section" className="py-24 relative overflow-hidden">
        <div className="container mx-auto px-6">
          {/* Trust Badges - Top Section */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8 max-w-5xl mx-auto">
            <div className="flex items-center gap-2 bg-muted/50 rounded-full px-4 py-2 shadow-sm hover:scale-105 transition-transform duration-200">
              <GlobeIcon className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Global Reach</span>
            </div>
            <div className="flex items-center gap-2 bg-muted/50 rounded-full px-4 py-2 shadow-sm hover:scale-105 transition-transform duration-200">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">SOC-2</span>
            </div>
            <div className="flex items-center gap-2 bg-muted/50 rounded-full px-4 py-2 shadow-sm hover:scale-105 transition-transform duration-200">
              <Lock className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">GDPR</span>
            </div>
            <div className="flex items-center gap-2 bg-muted/50 rounded-full px-4 py-2 shadow-sm hover:scale-105 transition-transform duration-200">
              <Key className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-foreground">End-to-End Encryption</span>
            </div>
          </div>

          {/* Main Security Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            {/* Left Column - Headline + Visual */}
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Trusted. Secure. 
                  <span className="bg-gradient-primary bg-clip-text text-transparent"> Proven.</span>
                </h2>
                <p className="text-muted-foreground text-lg">
                  Built for peace of mind, not just performance. Enterprise-grade security baked into every layer.
                </p>
              </div>

              {/* Security Dashboard Visual */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-primary-glow/20 blur-3xl" />
                <div className="relative bg-card/80 backdrop-blur-sm p-8 rounded-2xl border border-border/50 shadow-elegant transform perspective-1000 rotate-y-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-3 w-3 rounded-full bg-red-500" />
                    <div className="h-3 w-3 rounded-full bg-yellow-500" />
                    <div className="h-3 w-3 rounded-full bg-green-500" />
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="h-6 w-6 text-green-500" />
                      <div className="h-2 w-48 bg-green-500/20 rounded-full overflow-hidden">
                        <div className="h-full w-full bg-green-500 rounded-full" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Lock className="h-6 w-6 text-primary" />
                      <div className="h-2 w-36 bg-primary/20 rounded-full overflow-hidden">
                        <div className="h-full w-full bg-primary rounded-full" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Key className="h-6 w-6 text-primary-glow" />
                      <div className="h-2 w-44 bg-primary-glow/20 rounded-full overflow-hidden">
                        <div className="h-full w-full bg-primary-glow rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Security Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300">
                <ShieldCheck className="h-10 w-10 text-primary mb-4" />
                <h4 className="font-semibold mb-2 text-foreground">Zero Training Leakage</h4>
                <p className="text-sm text-muted-foreground">
                  Your data never trains AI models. What you create stays 100% yours.
                </p>
              </Card>

              <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300">
                <User className="h-10 w-10 text-primary mb-4" />
                <h4 className="font-semibold mb-2 text-foreground">You Own Everything</h4>
                <p className="text-sm text-muted-foreground">
                  Full IP ownership with enterprise-grade data protection and privacy controls.
                </p>
              </Card>

              <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300">
                <Lock className="h-10 w-10 text-primary mb-4" />
                <h4 className="font-semibold mb-2 text-foreground">AI-Ready Compliance</h4>
                <p className="text-sm text-muted-foreground">
                  SOC-2 Type II, GDPR, and global AI regulations built-in from day one.
                </p>
              </Card>

              <Card className="p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300">
                <Key className="h-10 w-10 text-primary mb-4" />
                <h4 className="font-semibold mb-2 text-foreground">Always Updated</h4>
                <p className="text-sm text-muted-foreground">
                  Latest models and security patches with zero upgrade costs or downtime.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Related Articles Section */}
    <Gallery6 heading="Related Articles" items={apiArticles.length > 0 ? apiArticles : [{
      id: "article-1",
      title: "Why Enterprises Need AI Marketing Specialists, Not Generalists",
      summary: "Agencies know the pressure better than anyone. Five clients, fifteen campaigns, fifty deliverables — all due yesterday.",
      url: "https://blog-gimmefy.squarespace.com/blog/why-agencies-need-ai-marketing-specialists-not-generic-tools",
      image: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/1758003583562-3PGOP5IM72LVQYR42JYS/result-1758003567934.png?content-type=image%2Fpng"
    }, {
      id: "article-2",
      title: "The Enterprise Guide to Keeping Global Marketing On-Brand",
      summary: "Global brands live and die by consistency. A customer in Tokyo, Paris, or São Paulo should recognise you instantly — not because of your logo, but because every word, every image, every campaign feels unmistakably yours.",
      url: "https://blog-gimmefy.squarespace.com/blog/the-enterprise-guide-to-keeping-global-marketing-consistent",
      image: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/1757936130595-FDLJI51ZGDL0G3E26D6B/Featured+Image+%282%29.jpg?content-type=image%2Fjpeg"
    }, {
      id: "article-3",
      title: "How Global Brands Use gimmefy to Scale Campaigns Across Markets",
      summary: "When you're a global brand, your biggest strength — scale — can also be your biggest weakness. With teams in Paris, Singapore, São Paulo, and beyond, staying consistent across regions is a daily battle.",
      url: "https://blog-gimmefy.squarespace.com/blog/how-global-brands-use-ai-for-marketing-content-that-wins",
      image: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/1757948512565-6CJPJJRD1TFOXOGAT6ZO/result-1757948385148.png?content-type=image%2Fpng"
    }, {
      id: "article-4",
      title: "How AI Brand Vaults Keep Marketing Consistent Across Channels?",
      summary: "Marketing today is a multi-channel circus. One day it's a LinkedIn thought-leadership post, the next it's a TikTok trend, an Instagram carousel, a YouTube pre-roll, and an email campaign — all happening at once.",
      url: "https://blog-gimmefy.squarespace.com/blog/how-ai-brand-vaults-keep-marketing-consistent-across-channels",
      image: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/1757931487587-K092XC5NVB6OMO32F4ZI/Featured+Image+%282%29.jpg?content-type=image%2Fjpeg"
    }, {
      id: "article-5",
      title: "How Startups Can Stretch Marketing Budgets Without Sacrificing Growth?",
      summary: "Startups are built on a paradox: you need growth to survive, but growth eats budget — fast. Founders quickly learn that marketing spend can feel like tossing money into a fire. Agencies are expensive. Freelancers multiply. Subscriptions stack up. And",
      url: "https://blog-gimmefy.squarespace.com/blog/how-startups-can-stretch-marketing-budgets-without-sacrificing-growth",
      image: "https://images.squarespace-cdn.com/content/6712145f4a2aba0c43ebb02d/1758001577291-UW7TG0PNDRUAYGX1Q8E8/result-1758001559286.png?content-type=image%2Fpng"
    }]} />

    <FooterWithNewsletter />
  </div>;
};
export default EnterprisePage;